import { stepFormSchemaKurz } from "./schema-kurz.js";
import { FIRMA_ROLLE_OPTIONS, FIRMA_VERBINDUNG_OPTIONS, RECHTSFORM_OPTIONS, } from "./formOptions.js";
import { VOLLMACHT_DOKUMENTE, VOLLMACHT_MAX_DATEIEN, vollmachtOrdner, vollmachtScanKey, } from "./vollmachten.js";
/**
 * Central mapping from answer-key → KMVS-internal "Anzeigebegriff".
 *
 * Source: `for-ai/0_Kundenanalyse mit Zuordnung zu Risiken u. Vertragsarten.xlsx`,
 * column "Anzeigebegriff im KMVS". Used as the short, business-facing row
 * label in the Auswertung view (and any other KMVS-internal surface).
 *
 * Add entries here as the Excel evolves; the helpers below inject the
 * value onto every FieldDef so consumers can read `field.anzeigeBegriff`
 * uniformly.
 */
const KEY_ANZEIGE = {
    // Stammdaten
    name: "Name / Firma",
    telefon: "Telefon",
    strasse: "Straße & Hausnummer",
    fax: "Fax",
    plz: "PLZ",
    mail: "E-Mail",
    stadt: "Ort",
    webseite: "Webseite",
    land: "Land",
    gespraechspartner: "Gesprächspartner",
    gespraechsdatum: "Gesprächsdatum",
    // Allgemeine Informationen zum Unternehmen
    gruendungsjahr: "Gründungsdatum",
    gruender: "Gründer",
    rechtsform: "Rechtsform",
    hr_nummer: "Handelsregister",
    hr_register: "Register",
    steuernummer: "Steuernummer",
    ust_id: "USt-IdNr.",
    finanzamt: "Finanzamt",
    vorsteuerabzug: "Vorsteuerabzug",
    geschaeftsjahr_kalender: "Geschäftsjahr",
    geschaeftsjahr_abweichung: "Geschäftsjahr Details",
    rolle_unternehmen: "Rolle des Unternehmens",
    verbund_ja: "Weitere Unternehmen",
    verbund_firmen: "Weitere Unternehmen - Firmierung",
    beteiligungen_firmen: "Unternehmensbeteiligungen - Firmierung",
    beteiligungen_ja: "Unternehmensbeteiligungen",
    gesellschafter_beteiligungen_ja: "Unternehmensbeteiligungen",
    rechtsvorgaenger_ja: "Rechtsvorgänger",
    rechtsvorgaenger_details: "Rechtsvorgänger Details",
    bilanzpflicht_ja: "Bilanzierungspflicht",
    pruefungspflicht: "Prüfungspflicht",
    wp_buchpruefer: "Wirtschaftsprüfer/Buchprüfer",
    // Gesellschafter und Geschäftsführer
    gf1_nachname: "Geschäftsführer I — Nachname",
    gf1_vorname: "Geschäftsführer I — Vorname",
    gf1_geburtsdatum: "Geschäftsführer I — Geburtsdatum",
    gf1_kontakt: "Geschäftsführer I — Kontakt",
    gf2_nachname: "Geschäftsführer II — Nachname",
    gf2_vorname: "Geschäftsführer II — Vorname",
    gf2_geburtsdatum: "Geschäftsführer II — Geburtsdatum",
    gf2_kontakt: "Geschäftsführer II — Kontakt",
    gs1_nachname: "Gesellschafter I — Nachname",
    // Risiko-Selbsteinschätzung
    wirtschaftssektor: "Wirtschaftssektor",
    weitere_taetigkeiten: "Weitere Tätigkeiten",
    haftungsrisiko: "Haftungsrisiko",
    ausfallrisiko: "Ausfall-, Finanz- und Liquiditäts-Risiken",
    rechtsstreitigkeiten: "Rechtsstreitigkeiten",
    umweltrisiken: "Umweltrisiken",
    // Vermögenswerte
    kbe: "Kaufmännische Betriebseinrichtung",
    tbe_elektronik: "Technische Betriebseinrichtung",
    handelsware: "Waren / Vorräte",
    grundstuecke: "Grundstücke",
    sonstige_vermoegen: "Sonstige Vermögenswerte",
    // Kennzahlen
    umsatz: "Umsatz",
    wareneinsatz: "Wareneinsatz",
    rohertrag: "Rohertrag",
    umsatzrendite: "Umsatzrendite",
    // Menschen
    mitarbeiter_gesamt: "Mitarbeiter gesamt",
    gleichzeitig_anwesend: "Gleichzeitig anwesend",
    bg_bruttojahreslohn: "Bruttojahreslohn- und Gehaltssumme nach BG Gesamt",
    leiharbeiter_ja: "Leiharbeiter",
    subunternehmer_ja: "Subunternehmer",
    freelancer_ja: "Freelancer",
    besonderheiten_menschen: "Besonderes für Menschen im Unternehmen",
    versorgungen_gf: "Spezielle Versorgungen, Absicherungen GF/Gesellschafter",
    entsendung_ja: "Auslandsentsendung",
    pkw_eigene_ja: "Private Pkw Nutzung für VN",
    dienstreisen_ja: "Geschäfts-/Dienst- u. Montagereisen",
    // Kooperation
    erwartung_berater: "Erwartungshaltung",
    wuensche_vertrag: "Besonderheiten Vertragsgestaltungen, Risiken, Schäden, Selbstbeteiligungen",
    zufriedenheit_makler: "Zufriedenheit mit bisherigem Makler / Vermittler / Berater / Hauptversicherer",
    zufriedenheit_vor: "Zufriedenheit mit bisherigem Makler, Vermittler, Berater und Hauptversicherer",
    zusatz_hinweise: "Zusätzliche Hinweise",
    freitext_kunde: "Zusätzliche Hinweise",
    // QM
    qm_ja: "Qualitätsmanagement",
    qm_welches: "Welches Qualitätsmanagement",
    // Fahrzeuge (Sub-Sektor)
    kfz_eigene_ja: "Eigene Fahrzeuge",
    kfz_fremde_ja: "Fremde Fahrzeuge",
    sonstige_fahrzeuge: "Sonstige Fahrzeuge",
    // Versand / Bezug / Transport
    messen_ja: "Messeteilnahme",
    // Haftungs- und Tätigkeitsrisiken
    schadenersatz_ja: "Betriebsstätten- und Produktrisiko",
    bauleistungen_ja: "Bauleistungen für Dritte",
    abfaelle_ja: "Abfälle (Entsorgung / Lagerung)",
    vertrauensschaeden_ja: "Böswillige Handlungen / Unterschlagung von Vertrauenspersonen / Dritten",
    // Recht und geistiges Eigentum
    sicherheiten_ja: "Haftung aus Bürgschaften, Avalen, Sicherheiten",
    rechtsstreit_historie_ja: "Bisherige rechtliche Auseinandersetzungen",
    // Ertragsausfall- und Cyber-Risiken
    bu_ja: "Betriebsunterbrechungsrisiko",
    cyberangriff_ja: "Cyberangriff",
    // Erforderliche Unterlagen (Excel R268–R273, Spalte F)
    unterlagen_organigramm: "Organigramm",
    unterlagen_bilanz: "Bilanz",
    unterlagen_agb_vertraege: "AGB / Verträge",
    unterlagen_genehmigungen: "Genehmigungen",
    unterlagen_brandschutzbericht: "Brandschutzbericht",
    unterlagen_fotos: "Besichtigung / Fotos",
    unterlagen_sonstige: "Sonstige Unterlagen",
};
/**
 * Decorate a field with its Anzeigebegriff from KEY_ANZEIGE (if known).
 * Pre-set `anzeigeBegriff` on the caller's field wins over the map.
 */
const withAnzeige = (field) => {
    if (field.anzeigeBegriff !== undefined)
        return field;
    const mapped = KEY_ANZEIGE[field.key];
    return mapped ? { ...field, anzeigeBegriff: mapped } : field;
};
// Helper: build a yes/no question with optional follow-up inputs.
const yn = (question, key, followUp, hint) => ({
    question,
    hint,
    field: withAnzeige({ key, type: "yesno" }),
    ...(followUp
        ? {
            showFollowUpWhen: { key, equals: "ja" },
            followUpFields: followUp.map(withAnzeige),
        }
        : {}),
});
// Helper: build a simple single-input text/textarea/etc. question.
const q = (question, field, hint) => ({
    question,
    hint,
    field: withAnzeige(field),
});
// Helper: build a multi-input question.
const qMulti = (question, fields, opts) => ({
    question,
    hint: opts?.hint,
    layout: opts?.layout,
    fields: fields.map(withAnzeige),
});
/**
 * Erlaubte Dateiendungen eines Upload-Slots — **gespiegelt** aus
 * `RISIKOANALYSE_ALLOWED_EXTENSIONS`
 * (`apps/api/src/risikoanalyse-upload.constants.ts`).
 *
 * Bewusst EINE Liste für alle Slots und bewusst genau die des Servers. Eine je
 * Slot engere Auswahl („Fotos nur als Bild") sähe ordentlich aus, wäre aber
 * eine zweite Wahrheit: Der Server nimmt die Datei an, das Formular verweigert
 * sie — und niemand könnte dem Berater sagen, welche der beiden Regeln gilt.
 * Der Server bleibt maßgeblich (Endung UND Magic Bytes); dieser Wert füllt nur
 * den Dateidialog und den Hinweistext am Slot.
 */
const UNTERLAGEN_ACCEPT = [
    ".pdf",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".heic",
    ".heif",
    ".tif",
    ".tiff",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
];
/**
 * Größe je EINZELNER Datei in MB — Spiegel des Server-Defaults
 * `RISIKOANALYSE_UPLOAD_FILE_SIZE_LIMIT_BYTES` (10 MB). Er liegt unter
 * `client_max_body_size 12m` des Frontend-nginx, damit eine zu große Datei die
 * deutsche Meldung der API bekommt und keine nginx-Fehlerseite.
 *
 * **Der Serverwert ist per Umgebungsvariable gleichen Namens überschreibbar.**
 * Wer sie setzt, muss diese Zahl mitziehen: Ein kleinerer Serverwert schickt
 * den Nutzer in multers englischen 413, ein grösserer lässt das Formular
 * Dateien verweigern, die der Server nähme. `risikoanalyse-upload.slots.spec.ts`
 * (apps/api) vergleicht beide Werte und wird bei Drift rot.
 */
const UNTERLAGEN_MAX_MB = 10;
/**
 * Ein Datei-Slot der Risikoanalyse.
 *
 * `optional: true` an JEDEM Slot: Wer keinen Brandschutzbericht hat, hat auch
 * keinen hochzuladen — seine Analyse ist trotzdem fertig. Ohne das Flag endete
 * jede Analyse ohne Unterlagen bei ~85 %, und der Nutzer suchte die fehlenden
 * 15 % (`computeFormProgress`, `progress.ts`).
 *
 * `maxFiles` und `dokumentOrdner` sind aus der Slot-Tabelle des Backends
 * (`RISIKOANALYSE_UPLOAD_SLOTS`) gespiegelt — der Server kennt dieselben Zahlen
 * und dieselben Ordnernamen und erzwingt sie unabhängig von dieser Datei.
 */
const uploadSlot = (key, label, ordner, maxFiles, anzeigeBegriff) => ({
    key,
    label,
    type: "documents",
    optional: true,
    accept: UNTERLAGEN_ACCEPT,
    maxFiles,
    maxSizeMb: UNTERLAGEN_MAX_MB,
    dokumentOrdner: ordner,
    ...(anzeigeBegriff ? { anzeigeBegriff } : {}),
});
/**
 * Eine Zeile des Abschnitts „Vollmachten u. Verfügungen": Bearbeitungsstand
 * links, Scan rechts.
 *
 * `layout: "two"` stellt beide nebeneinander — dreizehn Papiere untereinander
 * mit je zwei vollbreiten Blöcken ergäben einen Schritt, durch den man nur noch
 * scrollt.
 *
 * Den Scan bekommt NUR die Langform. Die Kurzform (Kundenportal, öffentlicher
 * Token) trägt in diesem Stand überhaupt keinen Datei-Slot, und der Server
 * weist die Vollmacht-Slots im Kundenkanal ausdrücklich ab (`nurBerater` in
 * `RISIKOANALYSE_UPLOAD_SLOTS`) — eine Kachel dort wäre eine Einladung, die
 * bei jedem Klick mit 400 endet. `schema-kurz.ts` baut dieselbe Zeile deshalb
 * ohne den zweiten Teil.
 */
const vollmachtFrage = (dokument) => ({
    question: dokument.titel,
    ...(dokument.hinweis ? { hint: dokument.hinweis } : {}),
    layout: "two",
    fields: [
        {
            key: dokument.key,
            label: "Stand",
            anzeigeBegriff: dokument.anzeigeBegriff,
            type: "dokumentStatus",
            optional: true,
            ...(dokument.pflicht ? { pflichtpapier: true } : {}),
        },
        uploadSlot(vollmachtScanKey(dokument.key), "Unterschriebene Fassung", vollmachtOrdner(dokument), VOLLMACHT_MAX_DATEIEN, `${dokument.anzeigeBegriff} — Scan`),
    ],
});
const REGISTER_OPTIONS = [
    { value: "HRA", label: "HRA — Handelsregister A" },
    { value: "HRB", label: "HRB — Handelsregister B" },
    { value: "PR", label: "PR — Partnerschaftsregister" },
    { value: "GnR", label: "GnR — Genossenschaftsregister" },
    { value: "VR", label: "VR — Vereinsregister" },
];
const ASSET_CATEGORIES = [
    { key: "kbe", label: "Kaufmännische Betriebseinrichtung" },
    {
        key: "tbe_elektronik",
        label: "Technische Betriebseinrichtung – Elektronik",
    },
    {
        key: "tbe_masch_stat",
        label: "Technische Betriebseinrichtung – Maschinen stationär",
    },
    {
        key: "tbe_masch_mob",
        label: "Technische Betriebseinrichtung – Maschinen mobil",
    },
    {
        key: "selbstfahrend",
        label: "Selbstfahrende Arbeitsmaschinen (z. B. Gabelstapler)",
    },
    {
        key: "handelsware",
        label: "Waren / Vorräte – Handelsware (wird ein- und weiterverkauft)",
    },
    { key: "rohhilfsstoffe", label: "Unfertige Erzeugnisse, Roh-/Hilfsstoffe" },
    { key: "halbfertig", label: "Halbfertig- und Fertigwaren" },
    { key: "kundeneigentum", label: "Kundeneigentum" },
    { key: "mietereinbauten", label: "Mietereinbauten" },
    { key: "baustellen", label: "Werte auf Baustellen" },
    {
        key: "wertsachen",
        label: "Wertsachen (Bargeld, Urkunden, Edelmetalle, Kunst …)",
    },
    { key: "immobilien", label: "Immobilien" },
    { key: "photovoltaik", label: "Photovoltaikanlagen" },
    { key: "ladestationen", label: "Ladestationen" },
    { key: "grundstuecke", label: "Grundstücke" },
    { key: "sonstige_vermoegen", label: "Sonstige Vermögenswerte" },
];
const BENEFIT_CATEGORIES = [
    { key: "sachbezug", label: "Sachbezug" },
    { key: "bike_leasing", label: "Bike-Leasing" },
    {
        key: "anwesenheitspraemie",
        label: "Anwesenheitsprämie (für Mitarbeiter ohne Krankheitstage)",
    },
    { key: "weihnachts_urlaubsgeld", label: "Weihnachts- und Urlaubsgeld" },
    { key: "aufmerksamkeiten", label: "Aufmerksamkeiten" },
    { key: "vermoegenswirksame", label: "Vermögenswirksame Leistungen" },
    { key: "entgeltumwandlung", label: "Entgeltumwandlung" },
    {
        key: "ag_finanzierte_av",
        label: "Arbeitgeberfinanzierte Altersversorgungen",
    },
    {
        key: "bkv_tagegeld_bu",
        label: "Betriebliche Krankenversicherung, Tagegeld, Berufsunfähigkeitsabsicherungen",
    },
    { key: "zeitwertkonten", label: "Zeitwertkonten" },
    { key: "sonst_sozial", label: "Sonstige Leistungen" },
];
const GF_FUNCTIONS = [
    { value: "Geschäftsführer", label: "Geschäftsführer" },
    { value: "Vorstand", label: "Vorstand" },
    { value: "Kaufmännischer Leiter", label: "Kaufmännischer Leiter" },
];
const GS_FUNCTIONS = [
    { value: "Gesellschafter", label: "Gesellschafter" },
    {
        value: "Geschäftsführender Gesellschafter",
        label: "Geschäftsführender Gesellschafter",
    },
];
// Exported for reuse (z. B. „Ansprechpartner bereinigen" → Verantwortlichkeit).
export const VERANT_FUNCTIONS = [
    "Steuern",
    "Recht",
    "Bank",
    "Notar",
    "IT",
    "Datenschutz",
    "Buchhaltung",
    "Sicherheit",
    "Brandschutz",
    "Compliance",
    "QM",
].map((v) => ({ value: v, label: v }));
export const stepFormSchema = {
    sections: [
        // ───────────────────────── 1. Unternehmensinformation ─────────────────────────
        {
            key: "unternehmen",
            title: "Unternehmensinformation",
            color: "orange",
            subSteps: [
                {
                    key: "stammdaten",
                    title: "Stammdaten",
                    description: "Bitte prüfen und gegebenenfalls korrigieren Sie die hinterlegten Stammdaten.",
                    questions: [
                        qMulti("Sind die hinterlegten Stammdaten korrekt?", [
                            { key: "name", label: "Name / Firma", type: "text" },
                            { key: "telefon", label: "Telefon", type: "text", format: "phone" },
                            { key: "strasse", label: "Straße & Hausnummer", type: "text" },
                            { key: "fax", label: "Fax", type: "text", format: "phone" },
                            { key: "plz", label: "PLZ", type: "text" },
                            { key: "mail", label: "E-Mail", type: "text", format: "email" },
                            { key: "stadt", label: "Ort", type: "text" },
                            { key: "webseite", label: "Webseite", type: "text", format: "url" },
                            { key: "land", label: "Land", type: "text" },
                        ], {
                            layout: "two",
                            hint: "Bitte alle Felder kontrollieren und ggf. anpassen.",
                        }),
                        yn("Sind die Stammdaten korrekt?", "stammdaten_korrekt"),
                        q("Wer ist Ihr Gesprächspartner für diese Analyse?", {
                            key: "gespraechspartner",
                            label: "Gesprächspartner (Name, Funktion)",
                            type: "text",
                        }),
                        q("An welchem Datum führen Sie die Analyse durch?", {
                            key: "gespraechsdatum",
                            label: "Gesprächsdatum",
                            type: "date",
                        }),
                    ],
                },
                {
                    key: "gruendung",
                    title: "Allgemeine Informationen zum Unternehmen und zur Gründung",
                    questions: [
                        q("Wann wurde Ihr Unternehmen gegründet?", {
                            key: "gruendungsjahr",
                            label: "Gründungsdatum",
                            type: "date",
                        }),
                        q("Wer war der Gründer des Unternehmens?", {
                            key: "gruender",
                            label: "Name des Gründers",
                            type: "text",
                        }),
                        q("Welche Rechtsform hat Ihr Unternehmen?", {
                            key: "rechtsform",
                            label: "Rechtsform",
                            type: "select",
                            options: RECHTSFORM_OPTIONS,
                        }),
                        {
                            question: "Welche Funktion hat Ihr Unternehmen?",
                            hint: "Mehrfachauswahl möglich.",
                            field: {
                                key: "rolle_unternehmen",
                                label: "Rolle des Unternehmens",
                                type: "checkboxes",
                                options: FIRMA_ROLLE_OPTIONS,
                            },
                        },
                        qMulti("Wie lautet die Handelsregisternummer und in welches Register ist das Unternehmen eingetragen?", [
                            {
                                key: "hr_nummer",
                                label: "Handelsregisternummer",
                                type: "text", format: "hrnummer",
                            },
                            {
                                key: "hr_register",
                                label: "Register",
                                type: "select",
                                options: REGISTER_OPTIONS,
                            },
                        ]),
                        qMulti("Wie lautet Ihre Steuernummer und Umsatzsteuernummer und welches Finanzamt ist für Ihr Unternehmen zuständig?", [
                            { key: "steuernummer", label: "Steuernummer", type: "text" },
                            {
                                key: "ust_id",
                                label: "Umsatzsteuer-Identifikationsnummer",
                                type: "text", format: "ustIdNr",
                            },
                            {
                                key: "finanzamt",
                                label: "Zuständiges Finanzamt",
                                type: "text",
                            },
                        ]),
                        yn("Ist das Unternehmen vorsteuerabzugsberechtigt?", "vorsteuerabzug"),
                        yn("Ist das Geschäftsjahr gleich dem Kalenderjahr?", "geschaeftsjahr_kalender"),
                        {
                            question: "Wann endet Ihr abweichendes Geschäftsjahr?",
                            showWhen: { key: "geschaeftsjahr_kalender", equals: "nein" },
                            field: {
                                key: "geschaeftsjahr_abweichung",
                                label: "Abweichendes Geschäftsjahr (Monat / Tag)",
                                type: "text",
                            },
                        },
                        yn("Gibt es einen Rechtsvorgänger des heutigen Unternehmens?", "rechtsvorgaenger_ja", [
                            {
                                key: "rechtsvorgaenger_details",
                                label: "An welchen, wann und wieso verändert?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sind Sie bilanzierungspflichtig?", "bilanzpflicht_ja", [
                            {
                                key: "pruefungspflicht",
                                label: "Unterliegen Sie zusätzlich der gesetzlichen Prüfungspflicht (§ 316 HGB)?",
                                type: "yesno",
                            },
                            {
                                key: "wp_buchpruefer",
                                label: "Wer ist Wirtschafts-/Buchprüfer?",
                                type: "text",
                            },
                        ]),
                    ],
                },
                {
                    key: "verbund",
                    title: "Unternehmensverbund u. Beteiligungen",
                    description: "Erfassen Sie hier alle Unternehmen, die zum Verbund gehören oder an denen eine Beteiligung besteht. Für jede Firma im Verbund wird später eine eigene Analyse ausgefüllt — die übrigen Abschnitte dieser Analyse beziehen sich weiterhin auf das oben genannte Hauptunternehmen.",
                    questions: [
                        yn("Gibt es weitere zum Unternehmensverbund gehörende Unternehmen?", "verbund_ja"),
                        {
                            question: "Unternehmen im Verbund",
                            hint: "Bitte je Unternehmen Firmierung, Anschrift, Rolle, Verbindung, Beteiligungsquote und Tätigkeit angeben.",
                            showWhen: { key: "verbund_ja", equals: "ja" },
                            field: {
                                key: "verbund_firmen",
                                type: "firmaEntries",
                                options: FIRMA_VERBINDUNG_OPTIONS,
                                functionOptions: FIRMA_ROLLE_OPTIONS,
                                withTaetigkeitFuer: true,
                            },
                        },
                        yn("Ist das Unternehmen an weiteren Unternehmen außerhalb des Unternehmensverbundes beteiligt?", "beteiligungen_ja"),
                        {
                            question: "Beteiligungen außerhalb des Unternehmensverbunds",
                            hint: "Bitte je Unternehmen Firmierung, Anschrift, Rolle, Verbindung, Beteiligungsquote und Tätigkeit angeben.",
                            showWhen: { key: "beteiligungen_ja", equals: "ja" },
                            field: {
                                key: "beteiligungen_firmen",
                                type: "firmaEntries",
                                options: FIRMA_VERBINDUNG_OPTIONS,
                                functionOptions: FIRMA_ROLLE_OPTIONS,
                            },
                        },
                    ],
                },
                {
                    key: "gesellschafter",
                    title: "Gesellschafter und Geschäftsführer",
                    description: "Bitte Nachname, Vorname, Geburtsdatum und Kontaktdaten angeben. Bei Gesellschaftern zusätzlich die Anteile.",
                    questions: [
                        {
                            question: "Geschäftsführer",
                            field: {
                                key: "geschaeftsfuehrer",
                                type: "personEntries",
                                description: "Bitte alle aktiven Geschäftsführer eintragen.",
                                functionOptions: GF_FUNCTIONS,
                            },
                        },
                        {
                            question: "Gesellschafter",
                            field: {
                                key: "gesellschafter",
                                type: "personEntries",
                                description: "Auch Firmen können Gesellschafter sein. Bitte die Anteile in % angeben.",
                                withAnteile: true,
                                functionOptions: GS_FUNCTIONS,
                            },
                        },
                        yn("Haben die Gesellschafter / Beteiligten weitere Unternehmensbeteiligungen?", "gesellschafter_beteiligungen_ja", [
                            {
                                key: "gesellschafter_beteiligungen_details",
                                label: "An welchen Unternehmen, mit welchen Quoten und was machen diese Unternehmen?",
                                type: "textarea",
                            },
                        ]),
                        yn("Sind Veränderungen zum Unternehmen, zu den Gesellschafterstrukturen oder Beteiligungen angedacht?", "veraenderungen_ja", [
                            {
                                key: "veraenderungen_details",
                                label: "Welche Veränderungen?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "verantwortlichkeiten",
                    title: "Verantwortlichkeiten und Zuständigkeiten fürs Unternehmen",
                    description: "Wer ist im Unternehmen für welche Bereiche zuständig? Extern angelegte Dienstleister werden bei der Auswertung als Firmen-Kontakt übernommen.",
                    questions: [
                        {
                            question: "Wer ist wofür im Unternehmen zuständig?",
                            hint: "Hierbei geht es darum, wer für bestimmte Bereiche (z.B. Recht, Steuern, IT, Datenschutz) im Unternehmen zuständig ist.",
                            field: {
                                key: "verantwortlichkeiten",
                                type: "verantEntries",
                                functionOptions: VERANT_FUNCTIONS,
                            },
                        },
                    ],
                },
                {
                    key: "taetigkeit_kunden",
                    title: "Tätigkeitsbereiche / Kunden",
                    questions: [
                        {
                            /*
                             * Steht VOR der Beschreibung, wie in der Kontaktmaske: Erst die
                             * Einordnung, dann der Text dazu. Nur in der Langform — ein Kunde
                             * ordnet seinen Betrieb nicht in einen Katalog mit 220 Blättern
                             * ein, und die Kurzform führt ohnehin keine Katalogfelder.
                             */
                            question: "In welche Branche und Spezialisierung fällt Ihr Unternehmen?",
                            hint: "Mehrere Branchen möglich; die erste gilt als Hauptbranche.",
                            field: {
                                key: "branchen",
                                label: "Branche und Spezialisierung",
                                type: "branchen",
                            },
                        },
                        q("Was macht Ihr Unternehmen genau? (Tätigkeit / Betriebscharakter)", {
                            key: "taetigkeit",
                            label: "Beschreibung",
                            type: "textarea",
                        }, "In eigenen Worten formulieren."),
                        q("Gibt es darüber hinaus noch etwas?", {
                            key: "taetigkeit_weiteres",
                            label: "Ergänzungen",
                            type: "textarea",
                        }),
                        yn("Haben Sie B2B-Kunden?", "kunden_b2b_ja", [
                            {
                                key: "kunden_b2b_details",
                                label: "Anteil / Details (z. B. Kommunal)",
                                type: "text",
                            },
                        ]),
                        yn("Haben Sie B2C-Kunden?", "kunden_b2c_ja", [
                            {
                                key: "kunden_b2c_details",
                                label: "Anteil / Details",
                                type: "text",
                            },
                        ]),
                        yn("Haben Sie sonstige Kundengruppen?", "kunden_sonstige_ja", [
                            {
                                key: "kunden_sonstige_details",
                                label: "Welche Kundengruppen?",
                                type: "text",
                            },
                        ]),
                        yn("Gibt es Abhängigkeiten zu Kunden?", "kunden_abh_ja"),
                        q("Wo ist der Vertriebs- und Kunden-Schwerpunkt?", {
                            key: "vertriebsschwerpunkt",
                            label: "Schwerpunkt",
                            type: "textarea",
                        }),
                        q("Wie informieren Sie Kunden über Neuerungen oder Sonstiges?", {
                            key: "kundenkommunikation",
                            label: "Kommunikationswege",
                            type: "textarea",
                        }),
                        q("Wer sind Ihre Hauptlieferanten und wie verteilen sich die Umsätze?", {
                            key: "hauptlieferanten",
                            label: "Lieferanten und Verteilung",
                            type: "textarea",
                        }),
                        yn("Gibt es Abhängigkeiten zu Lieferanten?", "lieferanten_abh_ja"),
                    ],
                },
            ],
        },
        // ───────────────────────── 2. Sachwerte ─────────────────────────
        {
            key: "sachwerte",
            title: "Sachwerte",
            color: "pillarOrange",
            subSteps: [
                {
                    key: "vermoegen",
                    title: "Vermögenswerte",
                    description: "Hat Ihr Unternehmen Vermögenswerte in den folgenden Kategorien? Bitte je Kategorie was, wo und Neuwert in € angeben.",
                    questions: [
                        ...ASSET_CATEGORIES.map((c) => yn(`Hat Ihr Unternehmen ${c.label}?`, `asset_${c.key}_ja`, [
                            { key: `asset_${c.key}_was`, label: "Was?", type: "text" },
                            { key: `asset_${c.key}_wo`, label: "Wo?", type: "text" },
                            {
                                key: `asset_${c.key}_wert`,
                                label: "Neuwert in €",
                                type: "text",
                                format: "currencyEUR",
                            },
                        ])),
                        yn("Sind Veränderungen, Erweiterungen oder Investitionen geplant?", "vermoegen_invest_ja", [
                            {
                                key: "vermoegen_invest_details",
                                label: "Welche?",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "fahrzeuge",
                    title: "Fahrzeuge",
                    questions: [
                        yn("Haben Sie eigene Kfz?", "kfz_eigene_ja", [
                            {
                                key: "kfz_eigene_was",
                                label: "Was für Fahrzeuge?",
                                type: "text",
                            },
                            { key: "kfz_eigene_anzahl", label: "Wie viele?", type: "text", format: "integer" },
                        ]),
                        yn("Sind fremde Kfz vorhanden?", "kfz_fremde_ja", [
                            {
                                key: "kfz_fremde_was",
                                label: "Was für Fahrzeuge?",
                                type: "text",
                            },
                            { key: "kfz_fremde_anzahl", label: "Wie viele?", type: "text", format: "integer" },
                        ]),
                        yn("Haben Sie sonstige Fahrzeuge?", "kfz_sonstige_ja", [
                            {
                                key: "kfz_sonstige_was",
                                label: "Was für Fahrzeuge?",
                                type: "text",
                            },
                            { key: "kfz_sonstige_anzahl", label: "Wie viele?", type: "text", format: "integer" },
                        ]),
                    ],
                },
                {
                    key: "transport",
                    title: "Versand / Bezug / Transport",
                    questions: [
                        yn("Versenden Sie Waren?", "versand_ja", [
                            {
                                key: "versand_wie",
                                label: "Wie kommen die Waren zum Empfänger?",
                                type: "textarea",
                            },
                            {
                                key: "versand_mittel",
                                label: "Mit welchen Mitteln (PKW, LKW, Lieferwagen, Anhänger, Bahn, Spedition, Schiff, Flugzeug, Paketservice …)?",
                                type: "text",
                            },
                            {
                                key: "versand_gefahr",
                                label: "Wer trägt die Gefahr?",
                                type: "text",
                            },
                            {
                                key: "versand_wert",
                                label: "Wert pro Transportmittel (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                        qMulti("Wie erhalten Sie nötige Betriebsmittel? (Bezüge)", [
                            {
                                key: "bezug_wie",
                                label: "Wie kommen die Bezüge zu Ihnen?",
                                type: "textarea",
                            },
                            {
                                key: "bezug_mittel",
                                label: "Mit welchen Mitteln?",
                                type: "text",
                            },
                            {
                                key: "bezug_gefahr",
                                label: "Wer trägt die Gefahr?",
                                type: "text",
                            },
                            {
                                key: "bezug_wert",
                                label: "Wert pro Transportmittel (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                        yn("Gibt es geplante Zwischenlagerungen?", "zwischenlager_ja", [
                            {
                                key: "zwischenlager_details",
                                label: "Beschreibung",
                                type: "text",
                            },
                        ]),
                        yn("Beauftragen Sie Speditionen/Frachtführer für vereinzelten Transport?", "spedition_ja"),
                        yn("Transportieren Sie auch für Dritte?", "transport_dritte_ja"),
                        yn("Unterhalten Sie ein Konsignationslager (fremde Ware)?", "konsignation_ja"),
                        yn("Sind Sie Lagerhalter? (Verwaltung fremder Waren gegen Entgelt — § 475 HGB)", "lagerhalter_ja"),
                        yn("Nehmen Sie an Messen und Ausstellungen teil?", "messen_ja"),
                    ],
                },
                {
                    key: "betriebsstaetten",
                    title: "Betriebsstätten und Lager",
                    description: "Diese Fragen beziehen sich auf den Hauptstandort. Weitere Standorte erfassen wir später separat.",
                    questions: [
                        qMulti("Wo befindet sich der Standort?", [
                            { key: "standort_ort", label: "Ort", type: "text" },
                            { key: "standort_plz", label: "PLZ", type: "text" },
                            { key: "standort_strasse", label: "Straße", type: "text" },
                            { key: "standort_hausnummer", label: "Hausnummer", type: "text" },
                        ]),
                        {
                            question: "Handelt es sich um Eigentum oder ist die Immobilie angemietet, gepachtet oder geleast?",
                            field: {
                                key: "standort_eigentum",
                                label: "Nutzungsart",
                                type: "select",
                                options: [
                                    { value: "eigentum", label: "Eigentum" },
                                    { value: "miete", label: "Miete" },
                                    { value: "pacht", label: "Pacht" },
                                    { value: "leasing", label: "Leasing" },
                                ],
                            },
                            showFollowUpWhen: {
                                key: "standort_eigentum",
                                notEquals: "eigentum",
                            },
                            followUpFields: [
                                {
                                    key: "standort_eigentuemer",
                                    label: "Wer ist Eigentümer? Wer trägt das Risiko an den genutzten Räumen?",
                                    type: "contactPicker",
                                },
                            ],
                        },
                        qMulti("Wie groß sind die Flächen?", [
                            {
                                key: "flaeche_gesamt",
                                label: "Fläche insgesamt (m²)",
                                type: "text", format: "area",
                            },
                            {
                                key: "flaeche_selbst",
                                label: "Davon selbst genutzt (m²)",
                                type: "text", format: "area",
                            },
                        ]),
                        yn("Erfolgt eine Untervermietung?", "untervermietung_ja"),
                        yn("Wurden Grundstücksbestandteile von Ihnen eingebracht?", "grundstueckbest_ja", [], "Ist fremdes Eigentum mitzuversichern?"),
                        q("Welche Heizung haben Sie am Standort?", {
                            key: "heizung",
                            label: "Heizungsart",
                            type: "text",
                        }),
                        yn("Ist ein Trafo vorhanden?", "trafo_ja"),
                        yn("Handelt es sich auch um den Firmensitz?", "firmensitz_ja"),
                        yn("Handelt es sich auch um die Postanschrift?", "postanschrift_ja"),
                        // Sicherungen 8 Stück + sonstiges
                        //
                        // `nachweis` markiert die vier Anlagen, für die es einen
                        // Attest-/Wartungsnachweis zum Hochladen gibt (Plan §8). Der Slot
                        // hängt als weiteres Folgefeld am vorhandenen Gate `sich_<key>_ja`
                        // — kein `showWhen`-Umbau, rein additiv: Wer die Anlage nicht hat,
                        // sieht auch den Nachweis nicht. Der Wert von `nachweis` ist das
                        // Ordnersegment unter „Anlagen-Nachweise/" und deckt sich mit
                        // `RISIKOANALYSE_UPLOAD_SLOTS` im Backend.
                        ...[
                            { key: "einfriedung", label: "Einfriedung" },
                            {
                                key: "ema",
                                label: "Einbruchmeldeanlage / Echtzeitüberwachung",
                                nachweis: "EMA",
                            },
                            { key: "bma", label: "Brandmeldeanlage", nachweis: "BMA" },
                            {
                                key: "rwa",
                                label: "RWA (Rauch- und Wärmeabzugsanlage)",
                                nachweis: "RWA",
                            },
                            {
                                key: "sprinkler",
                                label: "Sprinkleranlage",
                                nachweis: "Sprinkler",
                            },
                            { key: "stromabschaltung", label: "Stromabschaltung" },
                            { key: "video", label: "Videoüberwachung" },
                            { key: "notstrom", label: "Notstromversorgung" },
                        ].map((s) => yn(`Gibt es eine ${s.label}?`, `sich_${s.key}_ja`, [
                            { key: `sich_${s.key}_was`, label: "Was?", type: "text" },
                            { key: `sich_${s.key}_wo`, label: "Wo?", type: "text" },
                            {
                                key: `sich_${s.key}_wartung`,
                                label: "Wartung",
                                type: "text",
                            },
                            ...(s.nachweis
                                ? [
                                    uploadSlot(`sich_${s.key}_unterlagen`, "Unterlagen (Attest / Wartungsprotokolle)", `Anlagen-Nachweise/${s.nachweis}`, 10, 
                                    // Eigener Anzeigebegriff je Anlage: Vier Zeilen, die in
                                    // der Auswertung alle „Unterlagen (Attest / …)" hießen,
                                    // wären dort nicht auseinanderzuhalten.
                                    `${s.nachweis} — Unterlagen (Attest / Wartungsprotokolle)`),
                                ]
                                : []),
                        ])),
                        yn("Gibt es sonstige Sicherungen?", "sich_sonstige_ja", [
                            {
                                key: "sich_sonstige_details",
                                label: "Bitte beschreiben",
                                type: "textarea",
                            },
                        ]),
                        qMulti("In wie vielen Schichten arbeiten Sie? Welche Öffnungs- und Anwesenheitszeiten?", [
                            { key: "schichten", label: "Schichten", type: "text" },
                            {
                                key: "oeffnungszeiten",
                                label: "Öffnungs-/Anwesenheitszeiten",
                                type: "text",
                            },
                        ]),
                        yn("Gibt es einen Notfallplan im Schadenfall?", "notfallplan_ja"),
                        q("Wie lange benötigen Sie, um Ihren Betrieb nach einem Schadenfall wieder fortsetzen zu können?", {
                            key: "wiederanlaufzeit",
                            label: "Wiederanlaufzeit",
                            type: "text",
                            placeholder: "z. B. 2 Wochen",
                        }),
                        yn("Gibt es weitere Präventions- oder Schadenabwendungs-Maßnahmen?", "weitere_praevention_ja", [
                            {
                                key: "weitere_praevention_details",
                                label: "Welche Maßnahmen?",
                                type: "textarea",
                            },
                        ]),
                        {
                            question: "Immobilien-Detailanalyse erforderlich",
                            hint: "Aufgrund Ihrer Angaben benötigen wir für diese Immobilie später noch eine Detailanalyse. Sie finden diese in der Übersicht, nachdem Sie dieses Formular abgeschlossen haben.",
                            showWhenAll: [
                                { key: "standort_ort", isFilled: true },
                                { key: "standort_plz", isFilled: true },
                                { key: "standort_strasse", isFilled: true },
                                { key: "standort_eigentum", equals: "eigentum" },
                            ],
                        },
                    ],
                },
            ],
        },
        // ───────────────────────── 3. Haftung / Recht ─────────────────────────
        {
            key: "haftung",
            title: "Haftung / Recht",
            color: "blue",
            subSteps: [
                {
                    key: "haftung_taetigkeit",
                    title: "Haftungs- und Tätigkeitsrisiken",
                    questions: [
                        yn("Sind Sie Schadenersatzrisiken aus dem Betrieb, Ihrer Tätigkeit oder Ihren Produkten ausgesetzt?", "schadenersatz_ja"),
                        yn("Nehmen Sie Arbeiten auf fremden Grundstücken vor?", "fremde_grundstuecke_ja"),
                        yn("Führen Sie Lohnfertigungen durch?", "lohnfertigung_ja"),
                        yn("Nehmen Sie Sachen in Obhut?", "obhut_ja"),
                        yn("Arbeiten Sie an Fahrzeugen?", "arbeit_fahrzeuge_ja"),
                        yn("Nehmen Sie an Ausschreibungen teil?", "ausschreibungen_ja"),
                        qMulti("Stellen Sie Produkte selbst her, vertreiben Sie unter Ihrem Namen, oder importieren Sie außerhalb Europas?", [
                            {
                                key: "prod_herstellung",
                                label: "Eigene Herstellung?",
                                type: "yesno",
                            },
                            {
                                key: "prod_vertrieb_name",
                                label: "Vertrieb unter Ihrem Namen?",
                                type: "yesno",
                            },
                            {
                                key: "prod_import",
                                label: "Import außerhalb Europas?",
                                type: "yesno",
                            },
                        ]),
                        yn("Wird Ware von Ihnen weiterverarbeitet, ein- und/oder ausgebaut?", "weiterverarbeitung_ja"),
                        yn("Sagen Sie Produkteigenschaften zu?", "produktzusagen_ja"),
                        yn("Ist ein Rückruf von Produkten denkbar?", "rueckruf_ja"),
                        yn("Werden Waren / Produkte mit Ihrem Namen gelabelt?", "labeling_ja"),
                        yn("Montieren Sie Waren / Produkte selbst?", "montage_eigen_ja"),
                        yn("Beauftragen Sie Fremde mit der Montage?", "montage_fremd_ja"),
                        yn("Arbeiten Sie für die Auto-/Fahrzeugteile-/Luftfahrt-/Schifffahrtsindustrie und/oder Bahn?", "sensitive_branchen_ja", [
                            {
                                key: "sensitive_branchen_details",
                                label: "Welche Branchen, in welchem Anteil?",
                                type: "text",
                            },
                        ]),
                        yn("Erbringen Sie Bauleistungen für Ihr Unternehmen selbst oder auch für Dritte?", "bauleistungen_ja"),
                        yn("Welche Abfälle jegliche Art fallen an und müssen gesondert gelagert / entsorgt werden?", "abfaelle_ja", [
                            {
                                key: "abfaelle_details",
                                label: "Bitte beschreiben",
                                type: "textarea",
                            },
                        ]),
                        yn("Gibt es Nutztiere?", "nutztiere_ja"),
                        yn("Sind Fehler von Mitarbeitern im operativen Geschäft (Eigenschäden) denkbar?", "mitarbeiterfehler_ja"),
                        yn("Sind Fehler von Organen in folgenden Bereichen denkbar — Unternehmensführung und Lenkung (Organtätigkeit) oder operatives Geschäft?", "organhaftung_ja"),
                        yn("Sind böswillige Handlungen / Unterschlagung von Vertrauenspersonen / Dritten denkbar?", "vertrauensschaeden_ja"),
                        yn("Sind eigene oder fremde Montage-Objekte und -Projekte geplant oder in Ausführung?", "montage_projekte_ja"),
                        yn("Sind Sie beratend oder planend tätig für Dritte?", "beratung_planung_ja"),
                    ],
                },
                {
                    key: "umwelt",
                    title: "Umweltrisiken",
                    questions: [
                        yn("Sind Sie Schadenersatzrisiken aus Umweltgefährdung ausgesetzt? Gibt es Umweltrisiken oder Anlagen wie Öltank, Notstromaggregat, Öl-/Benzin-/Fettabscheider, Maschineninhalte oder brennbare Gase mit einem Gesamtgewicht ab 3 Tonnen?", "umwelt_grundrisiko_ja", [
                            {
                                key: "umwelt_grundrisiko_details",
                                label: "Bitte beschreiben",
                                type: "textarea",
                            },
                        ]),
                        yn("Befindet sich das Grundstück oder Teile davon in einem Wasserschutz-, Wassereinzugs- oder Überschwemmungsgebiet?", "wasserschutz_ja", [
                            {
                                key: "wasserschutz_details",
                                label: "Welches Gebiet?",
                                type: "text",
                            },
                        ]),
                        q("Welche Firmen befinden sich in der Nachbarschaft Ihres Betriebsgrundstücks?", {
                            key: "nachbarschaft",
                            label: "Branche, Entfernung",
                            type: "textarea",
                        }),
                        q("Welche Haftungssummen durch Gefährdung der Nachbarschaft – z. B. durch übergreifende Feuerschäden – sind denkbar?", {
                            key: "haftungspot_nachbarn",
                            label: "Einschätzung",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "vertrag_kunden",
                    title: "Vertragliche Vereinbarungen mit Kunden",
                    questions: [
                        q("Welche vertraglichen Vereinbarungen treffen Sie mit Ihren Kunden?", {
                            key: "vertragsarten",
                            label: "AGBs, Auftragsbestätigung, Dienstleistungs-/Werkverträge, Sonstige",
                            type: "textarea",
                        }, "Bitte alle relevanten Vereinbarungen später hochladen."),
                        yn("Weichen Sie von gesetzlichen Bestimmungen ab — Verlängerung der Verjährung der gesetzlichen Gewährleistung (BGB §§ 438 ff.)?", "verjaehrung_verlaengerung_ja"),
                        yn("Bedingen Sie die kaufmännische Prüf- und Rügepflicht ab?", "abbedingung_pruef_ja"),
                    ],
                },
                {
                    key: "recht_geistig",
                    title: "Recht und geistiges Eigentum",
                    questions: [
                        yn("Sind Sie Patentinhaber?", "patentinhaber_ja"),
                        yn("Sind Datenschutzverletzungen bei Ihnen denkbar?", "datenschutz_ja"),
                        yn("Ist der Verlust von Softwarelizenzen und/oder Daten möglich?", "verlust_lizenzen_ja"),
                        yn("Erteilen Sie Sicherheiten, Bürgschaften, Avale?", "sicherheiten_ja"),
                        yn("Hatten Sie schon mal mit rechtlichen Auseinandersetzungen zu tun?", "rechtsstreit_historie_ja", [
                            {
                                key: "rechtsstreit_historie_details",
                                label: "Bitte beschreiben",
                                type: "textarea",
                            },
                        ]),
                        yn("Sehen Sie Rechtsstreitigkeitsrisiken aus Urheberrechtsverletzungen, Patentrechtsverletzungen oder Dienstleistungs-/Werkverträgen?", "rechtsstreitrisiken_ja"),
                    ],
                },
            ],
        },
        // ───────────────────────── 4. Menschen im Unternehmen ─────────────────────────
        {
            key: "menschen",
            title: "Menschen im Unternehmen",
            color: "green",
            subSteps: [
                {
                    key: "belegschaft",
                    title: "Belegschaftsstruktur",
                    questions: [
                        qMulti("Wie viele Menschen arbeiten insgesamt für Ihr Unternehmen?", [
                            { key: "ma_gesamt", label: "Insgesamt", type: "text", format: "integer" },
                            { key: "ma_vollzeit", label: "Vollzeitkräfte", type: "text", format: "integer" },
                            {
                                key: "ma_monatsstunden",
                                label: "Monatsstunden Vollzeit",
                                type: "text", format: "decimal",
                            },
                            { key: "ma_teilzeit", label: "Teilzeitkräfte", type: "text", format: "integer" },
                            {
                                key: "ma_minijobber",
                                label: "Davon Minijobber",
                                type: "text", format: "integer",
                            },
                            {
                                key: "ma_midijobber",
                                label: "Davon Midijobber",
                                type: "text", format: "integer",
                            },
                            {
                                key: "ma_gleichzeitig",
                                label: "Zur gleichen Zeit",
                                type: "text", format: "integer",
                            },
                        ]),
                        qMulti("Wie hoch ist die Bruttojahreslohn-/Gehaltssumme nach BG-Meldung?", [
                            { key: "bg_gesamt", label: "In Gänze (€)", type: "text", format: "currencyEUR" },
                            {
                                key: "bg_buero",
                                label: "Büro / kfm. Angestellte (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "bg_gewerblich",
                                label: "Gewerbliche / technische MA (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "bg_aussendienst",
                                label: "Außendienst / Montage / Baustelle (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "bg_aushilfen",
                                label: "Aushilfen / Minijobber / Praktikanten (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "bg_gf_inhaber",
                                label: "Geschäftsführung / Inhaber (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ], { hint: "NICHT BWA — Daten der Berufsgenossenschaftsmeldung." }),
                    ],
                },
                {
                    key: "extern_mobilitaet",
                    title: "Externe Arbeitskräfte und Mobilität",
                    questions: [
                        yn("Arbeiten Sie mit Leiharbeitern?", "leiharbeiter_ja", [
                            {
                                key: "leiharbeiter_anzahl",
                                label: "Mit wie vielen?",
                                type: "text", format: "integer",
                            },
                            { key: "leiharbeiter_regel", label: "Regelmäßig?", type: "text" },
                        ]),
                        yn("Beauftragen Sie Subunternehmer?", "subunternehmer_ja", [
                            {
                                key: "subunternehmer_anzahl",
                                label: "Wie viele?",
                                type: "text", format: "integer",
                            },
                            {
                                key: "subunternehmer_anteil",
                                label: "Umsatzanteil",
                                type: "text",
                            },
                        ]),
                        yn("Beschäftigen Sie Freelancer?", "freelancer_ja", [
                            { key: "freelancer_anzahl", label: "Wie viele?", type: "text", format: "integer" },
                            { key: "freelancer_aufwand", label: "Aufwand", type: "text" },
                        ]),
                        qMulti("In welchen Bereichen sind die vorgenannten Personen in Ihrem Unternehmen tätig? (ca. %-Verteilung auf die Bereiche)", [
                            { key: "bereich_buero", label: "Büro", type: "text", format: "percent" },
                            {
                                key: "bereich_produktion",
                                label: "Produktion",
                                type: "text", format: "percent",
                            },
                            { key: "bereich_planung", label: "Planung", type: "text", format: "percent" },
                            {
                                key: "bereich_fremde",
                                label: "Arbeiten auf fremden Grundstücken",
                                type: "text", format: "percent",
                            },
                            { key: "bereich_montage", label: "Montage", type: "text", format: "percent" },
                        ]),
                        yn("Entsenden Sie von den vorgenannten Personen ins Ausland?", "entsendung_ja", [
                            { key: "entsendung_welche", label: "Welche?", type: "text" },
                            { key: "entsendung_wohin", label: "Wohin?", type: "text" },
                            {
                                key: "entsendung_dauer",
                                label: "Wie lange im Durchschnitt?",
                                type: "text",
                            },
                        ]),
                        yn("Fahren von den vorgenannten Personen welche mit dem eigenen PKW?", "pkw_eigene_ja", [
                            { key: "pkw_eigene_tage", label: "Tage im Jahr", type: "text" },
                            { key: "pkw_eigene_km", label: "Kilometer", type: "text" },
                        ]),
                        yn("Sind vorgenannte Personen auch auf Geschäfts- und Dienst- oder Montagereisen fürs Unternehmen unterwegs?", "dienstreisen_ja", [
                            { key: "dienstreisen_welche", label: "Welche?", type: "text" },
                            { key: "dienstreisen_wohin", label: "Wohin?", type: "text" },
                            { key: "dienstreisen_dauer", label: "Dauer", type: "text" },
                        ]),
                    ],
                },
                {
                    key: "leistungen_ma",
                    title: "Besondere Leistungen für Mitarbeiter",
                    description: "Tun Sie etwas Besonderes für die Menschen in Ihrem Unternehmen?",
                    questions: BENEFIT_CATEGORIES.map((b) => yn(`Bieten Sie ${b.label} an?`, `benefit_${b.key}_ja`, [
                        {
                            key: `benefit_${b.key}_details`,
                            label: "Bitte beschreiben",
                            type: "textarea",
                        },
                    ])),
                },
                {
                    key: "versorgung_gf",
                    title: "Versorgung Geschäftsführer / Gesellschafter",
                    questions: [
                        yn("Gibt es spezielle Versorgungen / Absicherungen für die Geschäftsführer und/oder Gesellschafter?", "gf_versorgung_ja", [
                            {
                                key: "gf_versorgung_details",
                                label: "Was, wo, in € (Neuwerte)",
                                type: "textarea",
                            },
                        ]),
                    ],
                },
                {
                    key: "tarif_mitgliedschaft",
                    title: "Tarifvertrag und Mitgliedschaften",
                    questions: [
                        yn("Versenden Sie einen Newsletter an Kunden oder Dritte?", "newsletter_ja", [
                            {
                                key: "newsletter_welcher",
                                label: "Welcher? (bitte später hochladen)",
                                type: "text",
                            },
                        ]),
                        yn("Unterliegen Sie einem Tarifvertrag?", "tarif_unterliegt_ja", [
                            {
                                key: "tarif_unterliegt_welcher",
                                label: "Welcher Tarifvertrag?",
                                type: "text",
                            },
                        ]),
                        yn("Orientieren Sie sich an einem Tarifvertrag?", "tarif_orientiert_ja", [
                            {
                                key: "tarif_orientiert_welcher",
                                label: "Welcher?",
                                type: "text",
                            },
                        ]),
                        q("Welcher IHK oder Handwerkskammer gehören Sie an?", { key: "ihk_hwk", label: "IHK / HWK", type: "text" }, "Pflicht — abhängig vom Betriebscharakter"),
                        yn("Sind Sie Mitglied in Berufsverbänden?", "berufsverbaende_ja", [
                            { key: "berufsverbaende_welche", label: "Welche?", type: "text" },
                        ]),
                        yn("Haben Sie sonstige Mitgliedschaften?", "mitgliedschaften_sonst_ja", [
                            {
                                key: "mitgliedschaften_sonst_welche",
                                label: "Welche?",
                                type: "text",
                            },
                        ]),
                    ],
                },
            ],
        },
        // ───────────────────────── 5. Liquidität / Ertragsausfall ─────────────────────────
        {
            key: "liquiditaet",
            title: "Liquidität / Ertragsausfall",
            color: "black",
            subSteps: [
                {
                    key: "kennzahlen",
                    title: "Kennzahlen des Unternehmens",
                    questions: [
                        qMulti("Wie hoch war/ist der Umsatz? (vorletztes / letztes / laufendes Jahr / Planung Folgejahr)", [
                            { key: "umsatz_v", label: "Vorletztes Jahr (€)", type: "text", format: "currencyEUR" },
                            { key: "umsatz_l", label: "Letztes Jahr (€)", type: "text", format: "currencyEUR" },
                            { key: "umsatz_a", label: "Laufendes Jahr (€)", type: "text", format: "currencyEUR" },
                            {
                                key: "umsatz_f",
                                label: "Planung Folgejahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                        yn("Gibt es starke Schwankungen beim Umsatz?", "umsatz_schwank_ja", [
                            {
                                key: "umsatz_schwank_warum",
                                label: "Warum?",
                                type: "textarea",
                            },
                        ]),
                        yn("Gibt es Sondereffekte (z. B. Sondergeschäfte) beim Umsatz?", "sondereffekte_ja", [
                            {
                                key: "sondereffekte_warum",
                                label: "Warum?",
                                type: "textarea",
                            },
                        ]),
                        yn("Gibt es Innenumsätze?", "innenumsaetze_ja", [
                            {
                                key: "innenumsaetze_details",
                                label: "Mit wem, wie hoch?",
                                type: "text",
                            },
                        ]),
                        qMulti("Wie verteilt sich der Umsatz auf die Regionen?", [
                            { key: "umsatz_de", label: "Deutschland", type: "text", format: "percent" },
                            { key: "umsatz_eu", label: "Europa", type: "text", format: "percent" },
                            { key: "umsatz_world", label: "Weltweit", type: "text", format: "percent" },
                            {
                                key: "umsatz_usa_kanada",
                                label: "Davon USA / Kanada",
                                type: "text", format: "percent",
                            },
                        ]),
                        ...[
                            "Automotive",
                            "Schifffahrt",
                            "Luftfahrt",
                            "Schienenverkehr",
                        ].map((br) => {
                            const k = br.toLowerCase();
                            return yn(`Generieren Sie Umsatz mit ${br}?`, `branche_${k}_ja`, [
                                {
                                    key: `branche_${k}_wieviel`,
                                    label: "Wieviel?",
                                    type: "text",
                                },
                                { key: `branche_${k}_warum`, label: "Warum?", type: "text" },
                            ]);
                        }),
                        qMulti("Wie hoch war/ist der Wareneinsatz?", [
                            {
                                key: "wareneinsatz_v",
                                label: "Vorletztes Jahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "wareneinsatz_l",
                                label: "Letztes Jahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "wareneinsatz_a",
                                label: "Laufendes Jahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "wareneinsatz_f",
                                label: "Planung Folgejahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                        yn("Wie hoch ist die Wareneinsatzquote (in %)? Gibt es Schwankungen?", "wareneinsatzquote_schwank_ja", [
                            {
                                key: "wareneinsatzquote",
                                label: "Wareneinsatzquote (%)",
                                type: "text", format: "percent",
                            },
                            {
                                key: "wareneinsatzquote_warum",
                                label: "Warum?",
                                type: "textarea",
                            },
                        ]),
                        qMulti("Wie hoch war/ist der Rohertrag?", [
                            {
                                key: "rohertrag_v",
                                label: "Vorletztes Jahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                            { key: "rohertrag_l", label: "Letztes Jahr (€)", type: "text", format: "currencyEUR" },
                            { key: "rohertrag_a", label: "Laufendes Jahr (€)", type: "text", format: "currencyEUR" },
                            {
                                key: "rohertrag_f",
                                label: "Planung Folgejahr (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                        q("Wie hoch ist Ihre Umsatzrendite?", {
                            key: "umsatzrendite",
                            label: "Umsatzrendite (%)",
                            type: "text", format: "percent",
                        }),
                        qMulti("Wie hoch ist Ihr Eigenkapital?", [
                            { key: "eigenkapital_quote", label: "Quote (%)", type: "text", format: "percent" },
                            { key: "eigenkapital_eur", label: "Höhe (€)", type: "text", format: "currencyEUR" },
                        ]),
                    ],
                },
                {
                    key: "liqu_verpflicht",
                    title: "Liquidität & Verpflichtungen",
                    questions: [
                        qMulti("Wie hoch ist Ihre verfügbare Liquidität?", [
                            { key: "liqu_kurz", label: "Kurzfristig (€)", type: "text", format: "currencyEUR" },
                            { key: "liqu_mittel", label: "Mittelfristig (€)", type: "text", format: "currencyEUR" },
                            { key: "liqu_lang", label: "Langfristig (€)", type: "text", format: "currencyEUR" },
                        ]),
                        qMulti("Wie hoch sind Ihre Verpflichtungen?", [
                            {
                                key: "verpfl_kk",
                                label: "Kontokorrent-Rahmen (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "verpfl_kredite",
                                label: "Kredite/Darlehen/Verbindlichkeiten",
                                type: "textarea",
                            },
                            {
                                key: "verpfl_sonstige",
                                label: "Sonstige Verpflichtungen",
                                type: "text",
                            },
                        ]),
                        yn("Geben Sie Bürgschaften?", "buergschaften_ja", [
                            {
                                key: "buergschaften_gesamt",
                                label: "Insgesamt (€)",
                                type: "text", format: "currencyEUR",
                            },
                            {
                                key: "buergschaften_schnitt",
                                label: "Im Schnitt (€)",
                                type: "text", format: "currencyEUR",
                            },
                        ]),
                    ],
                },
                {
                    key: "ertragsausfall_cyber",
                    title: "Ertragsausfall- und Cyber-Risiken",
                    questions: [
                        yn("Können Sie sich einen Betriebsunterbrechungsschaden bei Ihnen im Betrieb vorstellen und wodurch?", "bu_ja", [{ key: "bu_wodurch", label: "Wodurch?", type: "textarea" }]),
                        q("Wie bewerten Sie einen möglichen Stromausfall oder sonstige Versorgungsengpässe?", {
                            key: "stromausfall_bewertung",
                            label: "Einschätzung",
                            type: "textarea",
                        }),
                        yn("Würde eine Betriebsunterbrechung bei Zulieferern oder Abnehmern Auswirkungen bei Ihnen haben?", "bu_zuli_abn_ja"),
                        yn("Ist eine Betriebsschließung durch Behörden wegen Seuchengefahr denkbar?", "behoerd_seuche_ja"),
                        q("Wie würden Sie mit einem Datenverlust umgehen und würde dieser Ihren Betrieb beeinflussen?", { key: "datenverlust", label: "Einschätzung", type: "textarea" }),
                        yn("Sehen Sie für Ihr Unternehmen Risiken aufgrund von Cyber?", "cyberangriff_ja"),
                        q("Was verstehen oder verbinden Sie mit dem Begriff Cyber?", {
                            key: "cyber_verstaendnis",
                            label: "Cyber-Verständnis",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "forderungen_insolvenz",
                    title: "Forderungen, Zahlungen, Insolvenz",
                    questions: [
                        yn("Sind Zahlungsausfälle von Ihren Kunden möglich und denkbar?", "zahlungsausfall_ja"),
                        q("Wie haben Sie Ihr Mahnwesen aufgebaut?", {
                            key: "mahnwesen",
                            label: "Mahnwesen",
                            type: "textarea",
                        }),
                        yn("Sind schon mal Kunden- und/oder Zulieferer von einer Insolvenz betroffen gewesen?", "insolvenz_erfahrung_ja", [
                            {
                                key: "insolvenz_erfahrung_details",
                                label: "Bitte beschreiben",
                                type: "text",
                            },
                        ]),
                        yn("Sind Kostenrisiken aus rechtlichen Auseinandersetzungen denkbar?", "kostenrisiken_rechtsstreit_ja"),
                        yn("Sind Mietausfälle aus vermieteten Gebäuden / Räumlichkeiten abzusichern? Vermieten Sie zusätzlich unter?", "mietausfall_ja"),
                        yn("Gibt es Spezialmaschinen und/oder IT-Systeme mit längeren Lieferzeiten?", "spezialmaschinen_ja", [
                            {
                                key: "spezialmaschinen_details",
                                label: "Welche?",
                                type: "text",
                            },
                        ]),
                        yn("Könnten Sie bei einem befreundeten Unternehmen produzieren (Ausweichmöglichkeit)?", "ausweich_prod_ja"),
                        yn("Nutzen Sie Finanzierungen / Leasing / Mietkauf?", "finanzierung_ja", [
                            {
                                key: "finanzierung_details",
                                label: "Bitte beschreiben",
                                type: "textarea",
                            },
                        ]),
                        yn("Haben Sie ein professionelles Abrechnungs-/Rechnungsmanagement / Forderungsmanagement?", "forderungsmgmt_ja"),
                        yn("Nutzen Sie Factoring?", "factoring_ja"),
                    ],
                },
            ],
        },
        // ───────────────────────── 6. Erwartungshaltung & Unterlagen ─────────────────────────
        {
            key: "erwartung_unterlagen",
            title: "Erwartungshaltung & Unterlagen",
            color: "purple",
            subSteps: [
                {
                    key: "erwartung",
                    title: "Erwartungshaltung & Besonderheiten",
                    questions: [
                        q("Wie sieht Ihre Erwartungshaltung als möglicher Kunde gegenüber Ihrem Berater aus? Auf was sollte dieser besonders achten?", {
                            key: "erwartung_berater",
                            label: "Erwartungshaltung",
                            type: "textarea",
                        }),
                        q("Gibt es gewünschte Besonderheiten bei den Vertragsgestaltungen / Risiken / Schäden / Selbstbeteiligungen?", {
                            key: "wuensche_vertrag",
                            label: "Besonderheiten",
                            type: "textarea",
                        }),
                        q("Wie zufrieden waren Sie bisher mit Ihrem Makler / Vermittler / Berater und Hauptversicherer?", {
                            key: "zufriedenheit_vor",
                            label: "Zufriedenheit",
                            type: "textarea",
                        }),
                        q("Was möchten Sie uns gerne noch mit auf den Weg geben?", {
                            key: "freitext_kunde",
                            label: "Freitext",
                            type: "textarea",
                        }),
                    ],
                },
                {
                    key: "unterlagen",
                    title: "Nötige Unterlagen",
                    /*
                     * Modusblinder Text — dieses Schema serviert BEIDE Oberflächen.
                     *
                     * Der Beratermodus (`apps/web`) kann in die Slots hochladen, das
                     * Kundenportal (`apps/portal-web`) kann es nicht: dort rendert
                     * `FormField.tsx` sie als reine Anzeige, und einen Schreibweg gibt es
                     * bis AP10 nicht. Eine Upload-Zusage im Schrittkopf widerspräche für
                     * den Kunden jedem Slot darunter. Wo hochgeladen werden darf, sagt
                     * das die Stelle, die es weiss: `DocumentUploadField` zeigt Auflagen
                     * und Knopf nur bei Schreibrecht, sonst denselben Satz wie das Portal.
                     */
                    description: "Kreuzen Sie an, welche Unterlagen nachgereicht werden können. Darunter steht, was bereits vorliegt — leere Felder kosten keinen Fortschritt.",
                    questions: [
                        {
                            question: "Welche Unterlagen können Sie nachreichen?",
                            field: {
                                key: "unterlagen_checkliste",
                                type: "checkboxes",
                                options: [
                                    { value: "organigramm", label: "Organigramm" },
                                    { value: "bilanz", label: "Bilanz" },
                                    {
                                        value: "agb_vertraege",
                                        label: "AGB / Auftragsbestätigungen / Verträge mit Kunden",
                                    },
                                    { value: "brandschutzbericht", label: "Brandschutzbericht" },
                                ],
                            },
                        },
                        /*
                         * Der Block „Erforderliche Unterlagen" (Excel R268–R273) plus
                         * „Sonstige Unterlagen" — sieben Slots, Reihenfolge, Keys, Ordner
                         * und Obergrenzen wie in `RISIKOANALYSE_UPLOAD_SLOTS` im Backend.
                         *
                         * **Alle sieben sind IMMER sichtbar.** Die Ankreuzliste darüber ist
                         * ausdrücklich kein Sichtbarkeits-Gate: Sie deckt nur vier der
                         * sechs Excel-Pflichtzeilen ab, und ein Bestandsentwurf mit leerer
                         * Checkliste sähe sonst gar keinen Slot. Sie bleibt, was sie ist —
                         * eine Absichtserklärung.
                         */
                        qMulti("Unterlagen zur Risikoanalyse", [
                            uploadSlot("unterlagen_organigramm", "Organigramm", "Organigramm", 3),
                            uploadSlot("unterlagen_bilanz", "Bilanz", "Bilanz", 5),
                            uploadSlot("unterlagen_agb_vertraege", "AGB / Verträge", "AGB und Verträge", 10),
                            uploadSlot("unterlagen_genehmigungen", "Genehmigungen", "Genehmigungen", 10),
                            uploadSlot("unterlagen_brandschutzbericht", "Brandschutzbericht", "Brandschutzbericht", 5),
                            uploadSlot("unterlagen_fotos", "Besichtigung / Fotos", "Besichtigung und Fotos", 30),
                            uploadSlot("unterlagen_sonstige", "Sonstige Unterlagen", "Sonstige Unterlagen", 10),
                        ]),
                        q("Welche Genehmigungen liegen vor?", {
                            key: "genehmigungen",
                            label: "Genehmigungen von …",
                            type: "text",
                        }),
                        q("Welche sonstigen Unterlagen können Sie nachreichen?", {
                            key: "sonst_unterlagen",
                            label: "Sonstige Unterlagen",
                            type: "text",
                        }),
                    ],
                },
                {
                    key: "besichtigung",
                    title: "Besichtigung und Fotos",
                    description: "Vor-Ort-Besichtigung sowie Foto-Dokumentation der Betriebsstätten, Lager, Außenanlagen und sicherheits- und brandschutzrelevanter Einrichtungen.",
                    questions: [
                        yn("Wünschen Sie einen Vor-Ort-Besichtigungstermin?", "besichtigung_ja", [
                            {
                                key: "besichtigung_terminwunsch",
                                label: "Terminvorschlag (Datum/Uhrzeit)",
                                type: "text",
                            },
                        ]),
                        yn("Können Sie aktuelle Fotos der Betriebsstätten zur Verfügung stellen?", "fotos_ja", [
                            {
                                key: "fotos_hinweis",
                                label: "Hinweise (z. B. Anzahl, Bereiche)",
                                type: "text",
                            },
                        ]),
                    ],
                },
                {
                    key: "vollmachten",
                    title: "Vollmachten u. Verfügungen",
                    /*
                     * Excel R279–R299 (Stand 25.08.2026). Kein Fragebogen-Abschnitt im
                     * eigentlichen Sinn, sondern die Mappe des Mandats: Welche Papiere
                     * liegen unterschrieben vor, welche holt der Berater noch, welche
                     * fallen bei diesem Kunden nicht an.
                     *
                     * Die sieben „nur PK"-Zeilen der Excel fehlen bewusst — Begründung
                     * in `vollmachten.ts`.
                     */
                    description: "Halten Sie fest, welche Papiere unterschrieben vorliegen. Die unterschriebene Fassung können Sie direkt danebenlegen; nichts davon ist Voraussetzung, um die Analyse abzuschließen.",
                    questions: VOLLMACHT_DOKUMENTE.map(vollmachtFrage),
                },
            ],
        },
        // ───────────────────────── 7. Zusammenfassung ─────────────────────────
        {
            key: "zusammenfassung",
            title: "Zusammenfassung",
            color: "purple",
            subSteps: [
                {
                    key: "antworten_pruefen",
                    title: "Antworten prüfen",
                    description: "Übersicht aller Angaben — bitte prüfen Sie die Eingaben und korrigieren Sie ggf. einzelne Abschnitte.",
                    questions: [],
                    customKind: "summary",
                },
                {
                    key: "empfohlene_detailanalysen",
                    title: "Empfohlene Detailanalysen",
                    description: "Auf Basis Ihrer Antworten empfehlen wir die folgenden vertiefenden Analysen — gruppiert nach Risikobereich.",
                    questions: [],
                    customKind: "detail-analyses",
                },
                {
                    key: "auswertung_abschluss",
                    title: "Auswertung & Abschluss",
                    /*
                     * Der letzte Schritt des Beraters — und der Grund, warum es die
                     * eigene Auswertungsseite nicht mehr gibt.
                     *
                     * Bis hierher endete der Fragebogen mit „Absenden", warf den Berater
                     * zurück auf den Kontakt, und das eigentliche Festschreiben passierte
                     * auf einer zweiten Seite, die dieselben Felder ein zweites Mal
                     * ausbreitete. Wer sie nicht kannte, hatte seine Analyse nie
                     * übernommen: Stammdaten blieben unverändert, Detail-Risiken wurden
                     * nicht angelegt.
                     *
                     * Nur in der Langform. Der Kunde reicht ein, er schliesst nichts ab.
                     */
                    description: "Übernehmen Sie die erfassten Angaben in den Kontakt. Externe Zuständigkeiten lassen sich als Firmen-Kontakte anlegen, und für die empfohlenen Detailanalysen können Sie dem Kunden einen Erfassungs-Link schicken.",
                    questions: [],
                    customKind: "auswertung",
                },
            ],
        },
    ],
};
export const riskAnalysisSchemas = {
    "risk-analysis-short": stepFormSchemaKurz,
    "risk-analysis-advisor": stepFormSchema,
};
export const riskAnalysisSchemaVersions = {
    "risk-analysis-short": "2026-07-11",
    "risk-analysis-advisor": "2026-07-11",
};
